<template>
  <v-card class="mb-2">
    <v-row class="ml-4 mr-5 d-flex align-center">
      <span class="ml-3 mt-5 mr-4 font-weight-bold text-truncate"
        >Evasive maneuver {{ maneuver.id }}
        <span class="red--text">{{ maneuver.custom ? '(Custom)' : '' }}</span>
      </span>
      <EvasiveManeuverCesiumDialog
        :maneuverReport="unmodifiedManeuver"
        :id="'comparator' + unmodifiedManeuver.id"
        :key="'comparator' + unmodifiedManeuver.id"
      />
      <v-col col="1" align="right" style="padding-right: 0">
        <v-tooltip bottom>
          <template v-slot:activator="{ on: tooltip }">
            <v-btn
              icon
              @click="removeManeuverFromComparator(maneuver.id)"
              v-on="{ ...tooltip }"
            >
              <i
                aria-hidden="true"
                class="v-icon notranslate material-icons theme--light close"
              ></i>
            </v-btn>
          </template>
          <span>Remove from comparator</span>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-expansion-panels
      v-model="watchedPanelIndexes"
      class="pt-3"
      multiple
      accordion
      flat
      tile
    >
      <v-expansion-panel class="transparent" value="targetEvasiveManeuver">
        <v-expansion-panel-header class="outlined-panel">
          <span class="font-weight-bold mt-1 text-truncate"
            >Target evasive maneuver details
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="ml-7 mt-5 text-truncate">
            <DataPanel
              class="ml-3 mr-3 mb-0"
              :values="maneuver.targetReactionManeuverReport"
              :categories="evasiveManeuverCategories"
              :context="type"
              :bestValues="bestComparedValues"
            ></DataPanel>

            <div
              v-for="report in maneuver.targetReactionManeuverReport
                .targetImpulseManeuverReports"
              :key="report.id"
            >
              <DataPanel
                style="margin-top: 0px"
                class="ml-3 mr-3 mt-0"
                :values="report"
                :categories="deltaVVectorCategories"
                :context="'ImpulseManeuverReport'"
              ></DataPanel>
            </div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel class="transparent" v-show="displayDistanceToNominal">
        <v-expansion-panel-header class="outlined-panel">
          <span class="font-weight-bold mt-1 text-truncate"
            >Target distance to nominal trajectory
          </span>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <div class="ml-7 mt-5 text-truncate">
            <v-row class="ml-0" style="width: 100%; justify-content: center"
              ><FullscreenGraphComponent
                :traces="relativeDistanceToNominalTraces"
                :layout="relativeDistanceToNominalPlotLayout"
                title=""
                style="width: 100%; height: 300px"
                :maneuversDates="maneuversDates"
              ></FullscreenGraphComponent>
            </v-row></div
        ></v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel class="transparent" v-show="displayChaserReaction">
        <v-expansion-panel-header class="outlined-panel">
          <span class="font-weight-bold mt-1 text-truncate"
            >Chaser reaction maneuver details
          </span>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <div
            v-if="
              !maneuver.chaserReactionManeuverReport.relativeDistanceResults
            "
          >
            <span :style="{ color: 'orange' }"
              >No chaser reaction maneuver was found below the maximum chaser
              DeltaV constraint.
            </span>
          </div>
          <div
            class="ml-7 mt-5 text-truncate"
            v-if="maneuver.chaserReactionManeuverReport.relativeDistanceResults"
          >
            <DataPanel
              class="ml-3 mr-3 mb-0"
              :values="maneuver.chaserReactionManeuverReport"
              :categories="chaserReactionManeuverCategories"
              :context="type"
              :bestValues="bestComparedValues"
            ></DataPanel>
            <div
              v-for="report in maneuver.chaserReactionManeuverReport
                .chaserImpulseManeuverReports"
              :key="report.id"
            >
              <DataPanel
                style="margin-top: 0px"
                class="ml-3 mr-3 mt-0"
                :values="report"
                :categories="deltaVVectorCategories"
                :context="'ImpulseManeuverReport'"
              ></DataPanel>
            </div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel class="transparent" value="relativeDistance">
        <v-expansion-panel-header class="outlined-panel">
          <span class="font-weight-bold mt-1 text-truncate"
            >Relative distance between the two spacecrafts
          </span>
        </v-expansion-panel-header>

        <v-expansion-panel-content
          ><v-row class="ml-0" style="width: 100%; justify-content: center"
            ><FullscreenGraphComponent
              :traces="[
                ...relativeDistanceTraces,
                relativeDistance2DThresholdLegend
              ]"
              :layout="relativeDistanceToChaserPlotLayout"
              :title="
                'Evasive maneuver ' +
                maneuver.id +
                ': relative distance between the two spacecrafts'
              "
              :maneuversDates="maneuversDates"
              v-on:legendClick="toggleRelativeDistance2DTreshold($event)"
              style="width: 100%; height: 300px"
            ></FullscreenGraphComponent>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel class="transparent">
        <v-expansion-panel-header class="outlined-panel">
          <span class="font-weight-bold mt-1 text-truncate"
            >3D relative chaser position (target-centered)
          </span>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <v-row class="ml-0" style="width: 100%; justify-content: center"
            ><FullscreenGraphComponent
              :traces="[
                ...trajectory3dTraces,
                ...maneuverPoints,
                relativeDistance3DThreshold
              ]"
              :layout="trajectory3dPlotLayout"
              :title="
                'Evasive maneuver ' +
                maneuver.id +
                ': 3d relative distance between the two spacecrafts'
              "
              style="width: 100%; height: 300px"
            ></FullscreenGraphComponent>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel class="transparent">
        <v-expansion-panel-header class="outlined-panel">
          <span class="font-weight-bold mt-1 text-truncate"
            >Relative distance along each axis
          </span>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <EvasiveManeuverAxisComparator
            :noManeuverRelativeDistanceResults="
              referenceChaserToTargetRelativeDistanceResults
            "
            :targetManeuverRelativeDistanceResults="
              maneuver.targetReactionManeuverReport.relativeDistanceResults
            "
            :targetAndChaserManeuverRelativeDistanceResults="
              displayChaserReaction === true
                ? maneuver.chaserReactionManeuverReport.relativeDistanceResults
                : null
            "
            :targetRelativeDistanceAtBurns="targetRelativeDistanceAtBurns"
            :targetImpulseManeuverReports="targetImpulseManeuverReports"
            :chaserRelativeDistanceAtBurns="chaserRelativeDistanceAtBurns"
            :chaserImpulseManeuverReports="chaserImpulseManeuverReports"
            :fieldsToCompare="axisComparatorFields"
            :ranges="relativeDistanceRangesByAxis"
            :maneuversDates="maneuversDates"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>

<script>
import resultsDescription from '../../tools/resultsDescription.json'
import dashboardComponents from '../../components/Dashboard/index.js'
import dataTools from '../../tools/data.js'
import { mapGetters, mapActions } from 'vuex'
import Vue from 'vue'
import errorMixin from '../../mixins/logHandler.js'
import plotInput from '../../mixins/plotInput.js'
import plotLayout from '../../mixins/plotLayout.js'
import plotData from '../../mixins/plotData.js'
import { setTimeout } from 'timers'
import dashboardTools from '../../tools/dashboard'
import DataPanel from '../Defense/DataPanel'
import Plotly from 'plotly.js-dist'
import EvasiveManeuverCesiumDialog from './EvasiveManeuverCesiumDialog'
import FullscreenGraphComponent from './FullscreenGraphComponent'
import EvasiveManeuverAxisComparator from './EvasiveManeuverAxisComparator'

Vue.use(dashboardComponents)

export default {
  name: 'EvasiveManeuverComparatorCard',
  mixins: [errorMixin],
  components: {
    DataPanel,
    EvasiveManeuverCesiumDialog,
    FullscreenGraphComponent,
    EvasiveManeuverAxisComparator
  },
  data() {
    return {
      type: 'ReactionManeuverReport',
      showRelativeDistance2DThreshold: true,
      relativeDistanceThresholdColor: 'red',
      relativeDistanceThresholdOpacity: 0.3,
      relativeDistanceThresholdLegendText:
        'Minimum approach distance threshold',
      expandedPanels: [0, 1],
      deltaVVectorCategories: {
        '': {
          fields: [
            { fieldName: 'dateOfManeuver' },
            { fieldName: 'referenceFrame' },
            { fieldName: 'impulseDeltaV' },
            { fieldName: 'deltaVAlongXAxis' },
            { fieldName: 'deltaVAlongYAxis' },
            { fieldName: 'deltaVAlongZAxis' }
          ],
          noTitle: true,
          cardColor: 'transparent'
        }
      },

      chaserReactionManeuverCategories: {
        '': {
          fields: [
            { fieldName: 'chaserTotalDeltaV' },
            { fieldName: 'chaserReactionDuration' },
            { fieldName: 'closestApproachDistance' },
            { fieldName: 'closestApproachDate' }
          ],
          noTitle: true,
          cardColor: 'transparent'
        }
      },
      watchedPanelIndexes: []
    }
  },
  props: {
    panelIndexes: {
      type: Array
    },
    maneuver: {
      type: Object
    },
    unmodifiedManeuver: {
      type: Object
    },
    referenceChaserToTargetRelativeDistanceResults: {
      type: Object
    },
    bestComparedValues: {
      type: Object
    },

    relativeDistanceRangesByAxis: {
      type: Object
    },
    relativeDistanceToNominalRange: {
      type: Array
    },
    computeServiceRecovery: {
      type: Boolean,
      default: false
    },
    displayChaserReaction: {
      type: Boolean
    },
    displayDistanceToNominal: {
      type: Boolean
    },
    startDate: {
      type: String
    },
    endDate: {
      type: String
    }
  },

  mounted() {
    this.watchedPanelIndexes = this.panelIndexes
  },

  watch: {
    panelIndexes() {
      this.watchedPanelIndexes = this.panelIndexes
    },
    watchedPanelIndexes() {
      this.$emit('update-panel-indexes', this.watchedPanelIndexes)
    }
  },

  computed: {
    ...mapGetters({
      currentMission: 'currentMission'
    }),
    nominalTraceColor() {
      return this.$vuetify.theme.dark ? 'white' : '#1E1E1E'
    },
    evasiveTraceColor() {
      return '#5757ff'
    },
    evasiveAndReactionTraceColor() {
      return '#8B0000'
    },

    evasiveManeuverCategories() {
      let fields = [
        { fieldName: 'targetTotalDeltaV' },
        { fieldName: 'targetReactionDuration' }
      ]

      if (this.computeServiceRecovery == true) {
        fields = [...fields, { fieldName: 'targetServiceLossDuration' }]
      }

      if (this.currentMission.evasiveType === 'RPO_ESCAPE') {
        fields = [...fields, { fieldName: 'upwardThresholdCrossingDate' }]
      } else {
        fields = [
          ...fields,
          { fieldName: 'closestApproachDistance' },
          { fieldName: 'closestApproachDate' }
        ]
      }

      return {
        '': {
          fields: fields,
          noTitle: true,
          cardColor: 'transparent'
        }
      }
    },

    trajectory3dPlotLayout() {
      return {
        scene: {
          xaxis: {
            title: { text: 'T Distance (km)' }
          },
          yaxis: {
            title: { text: 'N Distance (km)' }
          },
          zaxis: {
            title: { text: 'W Distance (km)' }
          },
          margin: 0,
          aspectmode: 'manual'
        },
        margin: {
          l: 10,
          r: 10,
          b: 10,
          t: 40,
          pad: 4
        },
        height: 380
      }
    },

    minimumApproachDistance() {
      return this.currentMission?.minimumApproachDistance
    },

    relativeDistance2DThreshold() {
      const rectangle = {
        type: 'rect',

        xref: 'paper',
        x0: 0,
        x1: 1,

        yref: 'y',
        y0: 0,
        y1: this.minimumApproachDistance / 1000,
        fillcolor: this.relativeDistanceThresholdColor,
        opacity: this.relativeDistanceThresholdOpacity,
        line: {
          width: 0
        }
      }
      return [rectangle]
    },
    relativeDistance2DThresholdLegend() {
      return {
        x: [null],
        y: [null],
        type: 'bar',
        marker: {
          color: this.relativeDistanceThresholdColor,
          line: { color: 'transparent' }
        },
        opacity: this.relativeDistanceThresholdOpacity,
        name: this.relativeDistanceThresholdLegendText,
        showLegend: true
      }
    },

    relativeDistance3DThreshold() {
      const { x, y, z } = this.getPointsOnSphere(
        2000,
        this.minimumApproachDistance / 1000
      )
      const sphere = {
        opacity: this.relativeDistanceThresholdOpacity,
        color: this.relativeDistanceThresholdColor,
        type: 'mesh3d',
        alphahull: 0,
        name: this.relativeDistanceThresholdLegendText,
        showlegend: true,
        hoverinfo: 'skip',
        x,
        y,
        z
      }
      return sphere
    },

    relativeDistanceToChaserPlotLayout() {
      return {
        xaxis: {
          title: { text: 'Time (Days)' }
        },
        yaxis: {
          title: { text: 'Distance (km)', standoff: 5 },
          tickformat: '.g',
          range: this.relativeDistanceRangesByAxis.relativeDistanceEphemerides
        },
        margin: { t: 20, r: 30 },
        shapes: this.showRelativeDistance2DThreshold
          ? this.relativeDistance2DThreshold
          : []
      }
    },
    relativeDistanceToNominalPlotLayout() {
      return {
        xaxis: {
          title: { text: 'Time (Days)' }
        },
        yaxis: {
          title: { text: 'Distance (km)', standoff: 5 },
          tickformat: '.g',
          range: this.relativeDistanceToNominalRange
        },
        margin: { t: 20, r: 30 }
      }
    },

    trajectory3dTraces() {
      const totalSteps =
        this.maneuver.targetReactionManeuverReport.relativeDistanceResults
          .relativeDistanceT.length

      const traces = []
      traces.push(
        this.build3dTraceFromRelativeDistanceResult(
          this.maneuver.targetReactionManeuverReport.relativeDistanceResults,
          totalSteps,
          'Evasive maneuver',
          this.evasiveTraceColor
        )
      )
      traces.push(
        this.build3dTraceFromRelativeDistanceResult(
          this.referenceChaserToTargetRelativeDistanceResults,
          totalSteps,
          'No maneuver',
          this.nominalTraceColor
        )
      )

      traces.push({
        x: [0],
        y: [0],
        z: [0],
        text: ['Target space object'],
        hovertemplate:
          '%{text} ' +
          '<br><br><b>T</b>: %{x}' +
          '<br><b>N</b>: %{y}' +
          '<br><b>W</b>: %{z}',
        marker: {
          color: '#3be287',
          size: 6
        },
        name: 'Target',
        type: 'scatter3d',
        autosize: false,
        mode: 'markers'
      })

      if (
        this.displayChaserReaction &&
        this.maneuver.chaserReactionManeuverReport.relativeDistanceResults
      ) {
        traces.push(
          this.build3dTraceFromRelativeDistanceResult(
            this.maneuver.chaserReactionManeuverReport.relativeDistanceResults,
            totalSteps,
            'Evasive + Chaser reaction',
            this.evasiveAndReactionTraceColor
          )
        )
      }

      return traces
    },
    relativeDistanceTraces() {
      return this.buildAllRelativeDistanceTracesFromAxis(this.maneuver, null)
    },
    relativeDistanceToNominalTraces() {
      return [
        {
          x: this.maneuver.evasiveTargetToNominalRelativeDistanceResults
            .propagationDates,
          y: this.maneuver.evasiveTargetToNominalRelativeDistanceResults.relativeDistanceEphemerides.map(
            (x) => x / 1000
          ),
          marker: {
            color: this.evasiveTraceColor
          },
          name: 'Target distance to nominal trajectory'
        }
      ]
    },
    axisComparatorFields() {
      return [
        'relativeDistanceT',
        'relativeDistanceW',
        'relativeDistanceN',
        'propagationDates'
      ]
    },
    chaserImpulseManeuverReports() {
      return (
        this.maneuver.chaserReactionManeuverReport
          ?.chaserImpulseManeuverReports ?? []
      )
    },
    targetImpulseManeuverReports() {
      return (
        this.maneuver.targetReactionManeuverReport
          ?.targetImpulseManeuverReports ?? []
      )
    },
    targetManeuverDates() {
      return this.targetImpulseManeuverReports
        .map((report) => report.dateOfManeuver)
        .filter(
          (date) =>
            (this.startDate == null || date >= this.startDate) &&
            (this.endDate == null || date <= this.endDate)
        )
    },
    chaserManeuverDates() {
      return this.displayChaserReaction
        ? this.chaserImpulseManeuverReports
            .map((report) => report.dateOfManeuver)
            .filter(
              (date) =>
                (this.startDate == null || date >= this.startDate) &&
                (this.endDate == null || date <= this.endDate)
            )
        : []
    },
    maneuversDates() {
      return [
        {
          dates: this.targetManeuverDates,
          legendText: 'Target burns',
          color: this.evasiveTraceColor,
          opacity: 1
        },
        {
          dates: this.chaserManeuverDates,
          legendText: 'Chaser burns',
          color: this.evasiveAndReactionTraceColor,
          opacity: 1
        }
      ]
    },
    targetRelativeDistanceAtBurns() {
      return this.findRelativeDistanceAtBurns(
        this.maneuver.targetReactionManeuverReport.relativeDistanceResults,
        this.maneuver.targetReactionManeuverReport.targetImpulseManeuverReports
      )
    },
    targetManeuverPoints() {
      return [
        this.buildManeuver3Dpoints(
          this.targetRelativeDistanceAtBurns,
          this.maneuver.targetReactionManeuverReport
            .targetImpulseManeuverReports,
          'Target Burns',
          'blue'
        )
      ]
    },
    chaserRelativeDistanceAtBurns() {
      if (this.displayChaserReaction) {
        return this.findRelativeDistanceAtBurns(
          this.maneuver.chaserReactionManeuverReport.relativeDistanceResults,
          this.maneuver.chaserReactionManeuverReport
            .chaserImpulseManeuverReports
        )
      } else {
        return null
      }
    },
    chaserManeuverPoints() {
      if (this.displayChaserReaction) {
        return [
          this.buildManeuver3Dpoints(
            this.chaserRelativeDistanceAtBurns,
            this.maneuver.chaserReactionManeuverReport
              .chaserImpulseManeuverReports,
            'Chaser Burns',
            'red'
          )
        ]
      } else {
        return []
      }
    },
    maneuverPoints() {
      return [
        ...this.chaserManeuverPoints,
        ...this.targetManeuverPoints
      ].filter((points) => points != null)
    }
  },

  methods: {
    getPointsOnSphere(nbPoints, radius) {
      const goldenRatio = (1 + 5 ** 0.5) / 2
      const x = []
      const y = []
      const z = []
      for (let i = 0; i < nbPoints; i++) {
        const theta = (2 * Math.PI * i) / goldenRatio
        const phi = Math.acos(1 - (2 * (i + 0.5)) / nbPoints)
        x.push(Math.cos(theta) * Math.sin(phi) * radius)
        y.push(Math.sin(theta) * Math.sin(phi) * radius)
        z.push(Math.cos(phi) * radius)
      }
      return { x, y, z }
    },
    build3dTraceFromRelativeDistanceResult(
      relativeDistanceResults,
      totalSteps,
      name,
      color
    ) {
      return {
        x: relativeDistanceResults.relativeDistanceT.map((x) => x / 1000),
        y: relativeDistanceResults.relativeDistanceN.map((y) => y / 1000),
        z: relativeDistanceResults.relativeDistanceW.map((z) => z / 1000),
        text: relativeDistanceResults.relativeDistanceT.map(
          (_, index) =>
            '<b>Step: ' +
            (index + 1) +
            ' of ' +
            totalSteps +
            '</b><br><b>Date:</b> ' +
            relativeDistanceResults.propagationDates[index]
        ),
        hovertemplate:
          '%{text} ' +
          '<br><br><b>T</b>: %{x}' +
          '<br><b>N</b>: %{y}' +
          '<br><b>W</b>: %{z}',
        name: name,
        type: 'scatter3d',
        mode: 'lines',
        autosize: false,
        line: {
          color: color,
          width: 2
        }
      }
    },

    buildTracesFromRelativeDistanceResult(
      relativeDistanceResults,
      axis,
      name,
      color,
      line
    ) {
      return {
        x: relativeDistanceResults.propagationDates,
        y: relativeDistanceResults[
          'relativeDistance' + (axis ? axis : 'Ephemerides')
        ].map((x) => x / 1000),
        marker: {
          color: color
        },
        name: name,
        line: line ? line : undefined
      }
    },
    buildAllRelativeDistanceTracesFromAxis(maneuver, axis) {
      let traces = [
        this.buildTracesFromRelativeDistanceResult(
          maneuver.targetReactionManeuverReport.relativeDistanceResults,
          axis,
          'Evasive',
          this.evasiveTraceColor,
          null
        ),
        this.buildTracesFromRelativeDistanceResult(
          this.referenceChaserToTargetRelativeDistanceResults,
          axis,
          'No maneuver',
          this.nominalTraceColor,
          {
            dash: 'dot',
            width: 1
          }
        )
      ]

      if (
        this.displayChaserReaction &&
        maneuver.chaserReactionManeuverReport.relativeDistanceResults
      ) {
        traces.push(
          this.buildTracesFromRelativeDistanceResult(
            maneuver.chaserReactionManeuverReport.relativeDistanceResults,
            axis,
            'Evasive + Chaser reaction',
            this.evasiveAndReactionTraceColor,
            null
          )
        )
      }

      return traces
    },
    findRelativeDistanceAtBurns(
      relativeDistanceResults,
      impulseManeuverReports
    ) {
      const burnIndexes = []
      if (relativeDistanceResults == null || impulseManeuverReports == null) {
        return {
          relativeDistanceT: [],
          relativeDistanceN: [],
          relativeDistanceW: []
        }
      }

      const relativeDistanceDates = relativeDistanceResults.propagationDates
      for (const [
        reportIndex,
        impulseManeuverReport
      ] of impulseManeuverReports.entries()) {
        const maneuverDate = impulseManeuverReport.dateOfManeuver
        const closestDateIndex = relativeDistanceDates.findIndex(
          (date) => maneuverDate <= date
        )
        if (closestDateIndex > 0) {
          burnIndexes.push(closestDateIndex)
        } else {
          burnIndexes.push(null)
        }
      }

      return {
        relativeDistanceT: burnIndexes.map(
          (index) => relativeDistanceResults.relativeDistanceT[index]
        ),
        relativeDistanceN: burnIndexes.map(
          (index) => relativeDistanceResults.relativeDistanceN[index]
        ),
        relativeDistanceW: burnIndexes.map(
          (index) => relativeDistanceResults.relativeDistanceW[index]
        )
      }
    },

    buildManeuver3Dpoints(
      relativeDistanceAtBurns,
      impulseManeuverReports,
      name,
      color
    ) {
      if (relativeDistanceAtBurns == null || impulseManeuverReports == null) {
        return null
      }
      const { relativeDistanceT, relativeDistanceN, relativeDistanceW } =
        relativeDistanceAtBurns

      return {
        x: relativeDistanceT.map((distance) => distance / 1000),
        y: relativeDistanceN.map((distance) => distance / 1000),
        z: relativeDistanceW.map((distance) => distance / 1000),
        mode: 'marker',
        type: 'scatter3d',
        text: impulseManeuverReports.map((report, index) => {
          return (
            `Burn n°${index + 1}` +
            `<br><br><b>ΔV along T (m/s)</b>: ${dataTools.convertSi2Display(
              'velocity',
              report.deltaVAlongXAxis
            )}` +
            `<br><b>ΔV along N (m/s)</b>: ${dataTools.convertSi2Display(
              'velocity',
              report.deltaVAlongYAxis
            )}` +
            `<br><b>ΔV along W (m/s)</b>: ${dataTools.convertSi2Display(
              'velocity',
              report.deltaVAlongZAxis
            )}`
          )
        }),
        hovertemplate: '%{text} ',
        marker: {
          color: color,
          size: 5
        },
        line: {
          color: 'transparent'
        },
        name: name || 'Burns'
      }
    },

    toggleRelativeDistance2DTreshold(data) {
      if (
        data.data[data.curveNumber].name ===
        this.relativeDistanceThresholdLegendText
      ) {
        this.showRelativeDistance2DThreshold =
          !this.showRelativeDistance2DThreshold
      }
    },

    removeManeuverFromComparator(maneuverToRemoveId) {
      this.$emit('remove-maneuver', maneuverToRemoveId)
      return maneuverToRemoveId
    },

    getDisplayValue(context, fieldName, data) {
      const field = resultsDescription[context][fieldName]
      return dataTools.convertSi2Display(field.type, data[fieldName])
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Roboto');

.dialogContentContainer {
  padding: 30px;
  height: calc(100vh - 70px);
}

#fullScreenPlotDiv {
  width: 100%;
  height: 100%;
}

.light-custom-card {
  background-color: 'red';
}

.outlined-panel {
  border: 0px solid;
  border-top-width: 1px;
}
</style>
